import React, { useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { WaitlistForm } from "@/components/waitlist/WaitlistForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/seo/SEOHead";

export default function WaitlistPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const nursingHomeName = searchParams.get('nursing_home') || undefined;
  const countyName = searchParams.get('county') || undefined;
  const nursingHomeSlug = searchParams.get('nh_slug') || undefined;
  const {
    user
  } = useAuth();

  useEffect(() => {
    const justLoggedIn = sessionStorage.getItem('justCompletedAuth');
    if (justLoggedIn && user) {
      sessionStorage.removeItem('justCompletedAuth');
      toast.success(`Welcome ${user.user_metadata?.first_name || 'back'}!`, {
        description: "Please complete your waitlist registration.",
        duration: 5000
      });

      const formData = sessionStorage.getItem('waitlistFormData');
      if (formData && user) {
        try {
          const processStoredSubmission = async () => {
            const parsedData = JSON.parse(formData);
            const {
              data: existingSubmission,
              error: fetchError
            } = await supabase.from('waitlist_submissions').select('id').eq('user_id', user.id).limit(1);
            if (!existingSubmission?.length) {
              const {
                error: waitlistError
              } = await supabase.from('waitlist_submissions').insert({
                user_id: user.id,
                first_name: parsedData.firstName,
                last_name: parsedData.lastName,
                email: parsedData.email,
                phone: parsedData.phone,
                preferred_location: parsedData.preferredLocation,
                specific_nursing_home: parsedData.specificNursingHome || nursingHomeName,
                care_needs: parsedData.careNeeds,
                care_needs_details: parsedData.careNeedsDetails,
                mobility_requirements: parsedData.mobilityRequirements,
                resident_relation: parsedData.residentRelation,
                resident_salutation: parsedData.residentSalutation,
                resident_last_name: parsedData.residentLastName,
                resident_age: parsedData.residentAge,
                timeframe: parsedData.timeframe,
                fair_deal_scheme: parsedData.fairDealScheme,
                contact_preference: parsedData.contactPreference,
                additional_information: parsedData.additionalInformation
              });

              if ((parsedData.specificNursingHome || nursingHomeName) && !waitlistError) {
                const nhName = parsedData.specificNursingHome || nursingHomeName;
                const nhSlug = nursingHomeSlug || "unknown";
                const county = parsedData.preferredLocation?.[0] || countyName || '';
                await supabase.from('nursing_home_leads').insert({
                  user_id: user.id,
                  nursing_home_id: nhSlug,
                  nursing_home_name: nhName,
                  location: county,
                  status: 'waitlist',
                  notes: `Added via waitlist form. Timeframe: ${parsedData.timeframe}`
                });
              }

              sessionStorage.removeItem('waitlistFormData');
              toast.success("Your waitlist registration has been completed", {
                description: "View your dashboard to see the nursing homes you're interested in."
              });

              setTimeout(() => navigate('/dashboard'), 1500);
            }
          };
          processStoredSubmission();
        } catch (e) {
          console.error("Error restoring form data:", e);
        }
      }
    }
  }, [user, navigate, nursingHomeName, nursingHomeSlug, countyName]);

  return <Layout>
      <SEOHead 
        title="Join Nursing Home Waitlist | Grandpal"
        description="Register your interest in nursing homes and get personalized assistance with finding the right care solution."
        canonicalUrl="/waitlist"
      />

      <div className="container py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-grandpal-green-dark mb-4">Join Our Nursing Home Waitlist</h1>
            <p className="text-lg text-gray-700">
              Register your interest and let us help you find the perfect nursing home. We'll work with nursing homes on your behalf to secure a place.
            </p>
            {!user && <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800">
                  <strong>Note:</strong> You'll need to create an account to complete this process. This allows us to save your preferences and keep you updated.
                </p>
              </div>}
          </div>

          <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-grandpal-beige mb-8">
            <h2 className="text-xl md:text-2xl font-semibold mb-2">How It Works</h2>
            <ol className="list-decimal pl-5 space-y-2 mb-4">
              <li>Fill out the detailed form below with your preferences and requirements.</li>
              <li>Our specialist team will review your information.</li>
              <li>We'll reach out to suitable nursing homes on your behalf.</li>
              <li>You'll receive personalized options that match your needs.</li>
              <li>We'll provide guidance throughout the process until placement is secured.</li>
            </ol>
            <p className="text-sm text-gray-600 italic">Your information will only be shared with nursing homes you're interested in, with your explicit consent.</p>
          </div>

          <WaitlistForm nursingHomeName={nursingHomeName} countyName={countyName} nursingHomeSlug={nursingHomeSlug} />
        </div>
      </div>
    </Layout>;
}
